import React from "react";
import Seo from "../components/Seo";
import Loading from "../components/Loading";
import {Link} from "react-router-dom";
import "../css/Documentation.css";
import {BrokerBenchmark, parseBrokerResult} from "../components/BrokerBenchmark";
import connectionsUrl from "../xmq_test_results/Cluster-Connections.txt";
import fanInUrl from "../xmq_test_results/Cluster-Fan-In.txt";
import fanOutUrl from "../xmq_test_results/Cluster-Fan-Out.txt";
import p2pUrl from "../xmq_test_results/Cluster-P2P.txt";
import ramp from "../xmq_test_results/Cluster-Ramp.json";
import statefulMigration from "../xmq_test_results/Cluster-Stateful-Migration.json";

const cellStyle = {border: "1px solid #ccc", padding: "4px 8px", verticalAlign: "top"};
const RESULT_TEXT = {pass: "held", knee: "at the knee", saturated: "saturated"};
const RESULT_COLOURS = {pass: "#1a7f37", knee: "#9a6700", saturated: "#b42318"};

const TESTS = [
    {key: "connections", title: "500K Connections", url: connectionsUrl, targetRate: 5000,
     text: "500,000 clients connecting at 5,000/s, spread evenly over the four nodes: 125,000 each."},
    {key: "fanin", title: "Fan-In 50K", url: fanInUrl, targetRate: 50000,
     text: "50,000 publishers send one QoS 1 message each second to a cluster-wide shared subscription with 500 subscribers."},
    {key: "fanout", title: "Fan-Out 250K", url: fanOutUrl, targetRate: 250000,
     text: "Five publishers send 250 messages/second in total to 1,000 subscribers: 250,000 deliveries/second."},
    {key: "p2p", title: "Point-to-Point 50K", url: p2pUrl, targetRate: 100000,
     text: "50,000 publishers and 50,000 subscribers, one topic each, 2 messages/second per publisher: 100,000 messages/second."},
];

export default class XMQ_tests_cluster extends React.Component
{
    state = {results: {}, error: null};

    componentDidMount()
    {
        Promise.all(TESTS.map(t => fetch(t.url).then(r => r.text()).then(text => [t.key, parseBrokerResult(text)])))
            .then(pairs => this.setState({results: Object.fromEntries(pairs)}))
            .catch(err => this.setState({error: String(err)}));
    }

    render()
    {
        const {results, error} = this.state;
        return <div key="tests-cluster" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="MQTT Performance Tests — Cluster"
                 description="Four-node MQTT cluster benchmark: XMQ 1.0.0 and EMQX 5.8.9 under connections, fan-in, fan-out and point-to-point load on AWS."
                 keywords="MQTT cluster benchmark, MQTT performance tests, clustered MQTT broker, XMQ, EMQX"
                 path="/xmq_tests_cluster"/>
            <h1>MQTT Performance Tests: Cluster</h1>
            <h3>Four Nodes, One Client, One Shared Store</h3>

            <p>
                The XMQ cluster has four broker nodes, each on its own AWS <code>c5n.2xlarge</code>
                instance (8 vCPU, 21 GiB), in one placement group in <code>us-east-1a</code>. A single
                <code>c5n.4xlarge</code> client carries every connection, binding its clients to 29
                source addresses. XMQ keeps its cluster state in a dedicated Redis instance and its
                accounts in PostgreSQL; EMQX 5.8.9 is the last Apache&nbsp;2.0 release that can form a
                cluster without a commercial licence. Both products run MQTT 5 on port 1884,
                QoS&nbsp;1, with a 16-byte payload.
            </p>
            <p>
                The comparison covers only brokers that can form a cluster in their community
                editions. XMQ and EMQX 5.8.9 both do so without a commercial licence; brokers whose
                community editions do not support clustering were not tested here, because a
                single-node result would not answer the question this page asks.
            </p>
            <p>
                Every scenario starts on a freshly restarted cluster, with the other broker stopped.
                Each node is a separate process on a separate machine, so the numbers include the
                cluster links, not just one broker's event loop. Latency is end-to-end delivery;
                memory and CPU are the sum of the four nodes. See the
                {" "}<Link to="/xmq_tests_environment">Test Environment</Link> page for the single-node
                method and the client.
            </p>
            <p>
                The four throughput scenarios use MQTT clean sessions (<code>clean_session=true</code>).
                They measure routing and delivery throughput, and a clean session leaves no state to
                carry when a node goes down, so those runs say nothing about session migration. That
                behaviour is measured separately, below.
            </p>

            <p>
                Throughput is only half of what a cluster has to do. What happens when a node dies
                under load &mdash; whether the survivors keep serving and the victim comes back &mdash;
                is on the <Link to="/xmq_tests_chaos">Cluster Survivability</Link> page.
            </p>

            {error && <p>Failed to load test results: {error}</p>}
            {!error && TESTS.map(test =>
                <section key={test.key} style={{marginTop: 28}}>
                    <h4>{test.title}</h4>
                    <p>{test.text}</p>
                    {results[test.key]
                        ? <BrokerBenchmark servers={results[test.key]} targetRate={test.targetRate}/>
                        : <Loading text="Loading results…"/>}
                </section>)}
            <section style={{marginTop: 34}}>
                <h4>Stateful session migration</h4>
                <p>
                    This check is separate from the four throughput scenarios and does not use a clean
                    session. A subscriber with <code>clean_session=false</code> and a fixed client id
                    connected to <code>{statefulMigration.target_node}</code>. That node was then killed
                    with <code>kill -9</code>, as in the survivability test: no handover and no lease
                    released. While it was down, {statefulMigration.messages.toLocaleString()} unique
                    QoS&nbsp;1 messages were published at {statefulMigration.publish_rate_per_second}/s
                    from <code>{statefulMigration.publisher_node}</code>. The same client id then
                    reconnected to <code>{statefulMigration.recovery_node}</code>.
                </p>
                <table style={{borderCollapse: "collapse", margin: "8px 0 16px 0"}}>
                    <tbody>
                    <tr>
                        {["Measured", "Result"].map(title =>
                            <td key={title} style={{border: "1px solid #ccc", padding: "4px 8px", background: "#f0f0f0", fontWeight: "bold"}}>{title}</td>)}
                    </tr>
                    <tr>
                        <td style={cellStyle}>Messages published while the owner node was down</td>
                        <td style={cellStyle}>{statefulMigration.messages.toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td style={cellStyle}>Delivered after reconnecting to another node</td>
                        <td style={cellStyle}>{statefulMigration.delivered.toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td style={cellStyle}>Missing</td>
                        <td style={cellStyle}>{statefulMigration.missing}</td>
                    </tr>
                    <tr>
                        <td style={cellStyle}>Duplicates</td>
                        <td style={cellStyle}>{statefulMigration.duplicates}</td>
                    </tr>
                    <tr>
                        <td style={cellStyle}>
                            Also received by a live clean subscriber on <code>{statefulMigration.observer_node}</code>
                        </td>
                        <td style={cellStyle}>{statefulMigration.observer_delivered.toLocaleString()}</td>
                    </tr>
                    <tr>
                        <td style={{...cellStyle, fontWeight: "bold"}}>Result</td>
                        <td style={{...cellStyle, fontWeight: "bold", color: "#1a7f37"}}>{statefulMigration.result}</td>
                    </tr>
                    </tbody>
                </table>
                <p>
                    Every message published during the outage reached the persistent session on the node
                    it reconnected to, with no loss and no duplicates. The live subscriber shows that
                    publishing itself continued while the owner was gone. QoS&nbsp;1 is at-least-once, so
                    a retransmission would have been allowed; none occurred.
                </p>
            </section>
            <section style={{marginTop: 34}}>
                <h4>Connection-rate headroom (XMQ only)</h4>
                <p>
                    The 500K scenario above paces 5,000 connections per second, because that is a pace
                    both products sustain and a comparison has to be like for like. This is the other
                    question: how fast can XMQ accept connections before something in the stand gives.
                    The same 500,000 clients, at four rising paces, each on a freshly restarted cluster.
                </p>
                <table style={{borderCollapse: "collapse", margin: "8px 0 16px 0"}}>
                    <tbody>
                    <tr>
                        {["Pace asked for", "Sustained", "Median connect", "Result"].map(title =>
                            <td key={title} style={{border: "1px solid #ccc", padding: "4px 8px", background: "#f0f0f0", fontWeight: "bold"}}>{title}</td>)}
                    </tr>
                    {(ramp.rungs || []).map(rung =>
                        <tr key={rung.rate}>
                            <td style={cellStyle}>{rung.rate.toLocaleString()}/s</td>
                            <td style={cellStyle}>{(rung.steady_rate || 0).toLocaleString()}/s</td>
                            <td style={cellStyle}>
                                {rung.median_us >= 1000 ? `${(rung.median_us / 1000).toFixed(1)} ms` : `${rung.median_us} µs`}
                            </td>
                            <td style={{...cellStyle, color: RESULT_COLOURS[rung.result] || "inherit"}}>
                                {RESULT_TEXT[rung.result] || rung.result}
                            </td>
                        </tr>)}
                    </tbody>
                </table>
                <p>
                    XMQ holds 10,000/s with sub-millisecond connect latency. At 20,000/s the clients
                    still all connect, but half a second of queue has built up behind them, and above
                    that the stand settles near 21,000/s however fast the scenario asks. Neither the
                    brokers nor the client are the constraint: each broker stays under one core of its
                    eight and the client's 16 vCPUs sit mostly idle. The connection path is: it makes
                    about seven round trips to the shared Redis per connection &mdash; 3.5 million
                    commands for 500,000 clients, measured with <code>INFO commandstats</code> &mdash;
                    and a round trip to that Redis is about 0.1 ms on this stand. With four listener
                    threads per node, that is roughly 4,500 connections/s per node, or about 18,000 for
                    the cluster, which is where the ladder lands. Redis itself stays near half a core:
                    the cost is the number of round trips, not its CPU. Taking session registration
                    off the connect path, or batching it, is what would raise this ceiling.
                </p>
            </section>
        </div>;
    }
}
